import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, defer, from, map, Observable, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import type { ITranscriptSegment } from '../shared/helpers/chip-events';
import type {
  ContentBlock,
  IPublishBody,
  IQuestionSet,
  IStartInterviewBody,
  IVisualGalleryEntry,
  UserContent,
} from '../shared/models';
import { AuthService } from './auth/auth.service';
import type { IExportMeta } from './export.service';

// Payload del job BE "Profilo Architettonico" (v. be/proxy-agcasabito/src/generation): il FE
// prepara system prompt, contesto e tavole (li ha in memoria al click), il BE esegue le due
// chiamate Claude anche a tab chiusa E genera/carica il PDF completo (per questo servono anche
// cliMd, meta e galleria). Il modello lo aggiunge ApiService (environment.model).
export interface IArchJob {
  system: string;
  blocks: ContentBlock[];
  cruscotto: { text: string; maxTokens: number };
  ambienti: { text: string; maxTokens: number };
  cliMd: string;
  esiti?: { nCongruenti: number; nChiariti: number; nNonChiariti: number } | null;
  meta: IExportMeta;
  gallery: IVisualGalleryEntry[];
  // Scheda compilata da form → il BE genera {code}_scheda.pdf; null = scheda caricata come file.
  schedaText: string | null;
}

export interface ITranscription {
  text: string;
  segments: ITranscriptSegment[];
}

// v. Correzione 19 (06_profilo_architettonico.tex): diagnostica temporanea per capire se il
// prompt caching sposta davvero i tempi di Fase B, o se la variazione osservata è normale
// variabilità di lunghezza dell'output. cache_read/creation_input_tokens sono presenti solo se
// il breakpoint ha effettivamente scritto/letto qualcosa (v. Correzione 18).
interface IUsage {
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
}

// Unico punto di contatto col proxy NestJS (AI Claude su /proxy + Whisper su /proxy/transcribe).
// Il backend richiede il JWT Supabase (Authorization: Bearer) su ogni rotta.
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _auth: AuthService = inject(AuthService);
  private readonly _base: string = environment.workerUrl;

  // Generazione AI: streaming SSE col fallback JSON; emette il testo finale e completa.
  public generate(sys: string, content: UserContent, maxTokens: number): Observable<string> {
    return defer(() => from(this._generate(sys, content, maxTokens)));
  }

  // Trascrizione vocale (Whisper) di un blob audio. verbose_json → segmenti con timestamp
  // (secondi dall'inizio di questa registrazione), per collocare i tap della pulsantiera nel
  // punto esatto della trascrizione in cui sono avvenuti.
  public transcribe(blob: Blob, ext: string): Observable<ITranscription> {
    const fd: FormData = new FormData();
    fd.append('file', blob, `audio.${ext}`);
    fd.append('model', 'whisper-1');
    fd.append('language', 'it');
    fd.append('response_format', 'verbose_json');
    return this._http
      .post<{ text?: string; segments?: { start: number; end: number; text: string }[] }>(
        `${this._base}/proxy/transcribe`,
        fd,
        { headers: this._authHeaders() },
      )
      .pipe(
        map((d) => ({
          text: (d.text || '').trim(),
          segments: (d.segments || []).map((s) => ({ start: s.start, end: s.end, text: s.text })),
        })),
      );
  }

  // Avvia il job BE del Profilo Architettonico: risponde 202 subito, l'esito arriva su
  // interview_generation_app (il FE fa polling via GenerationService, non su questa rotta).
  public startArchJob(interviewId: string, job: IArchJob): Observable<{ started: boolean }> {
    return this._http.post<{ started: boolean }>(
      `${this._base}/generation/${interviewId}/start`,
      { model: environment.model, ...job },
      { headers: this._authHeaders() },
    );
  }

  // Rigenera SOLO il PDF sul server (nessuna chiamata Claude: cli_md/arch_md li legge da DB).
  // Sincrona: il BE risponde a upload concluso (~secondi). Usata dal bottone su /profile e
  // dopo la rigenerazione post-rating.
  public regenPdf(
    interviewId: string,
    body: { meta: IExportMeta; gallery: IVisualGalleryEntry[]; schedaText: string | null },
  ): Observable<{ ok: boolean }> {
    return this._http.post<{ ok: boolean }>(
      `${this._base}/generation/${interviewId}/pdf`,
      body,
      { headers: this._authHeaders() },
    );
  }

  // Documento singolo (copertina + testo/markdown) → PDF, renderizzato dal BE con lo stesso
  // motore dei file d'archivio di /interviews. Solo download, nulla viene salvato.
  public documentPdf(subtitle: string, body: string, meta: IExportMeta): Observable<Blob> {
    return this._http.post(
      `${this._base}/generation/document-pdf`,
      { subtitle, body, meta },
      { headers: this._authHeaders(), responseType: 'blob' },
    );
  }

  // Set del questionario già annidato (questions[].sub_questions[], question_app con fk_parent_id
  // risolto dal BE). setId null = set corrente; un id = la versione fissata a un'intervista.
  // null se il BE risponde 404 (nessuna versione pubblicata, o id inesistente).
  public getQuestionSet(setId: string | null = null): Observable<IQuestionSet | null> {
    const url = null === setId ? `${this._base}/question-set` : `${this._base}/question-set/${setId}`;
    return this._http
      .get<IQuestionSet>(url, { headers: this._authHeaders() })
      .pipe(catchError((e: HttpErrorResponse) => (404 === e.status ? of(null) : throwError(() => e))));
  }

  // Pubblica una nuova versione del questionario relazionale (migrazione 0031): il BE scrive
  // set, domande, sottodomande e junction in una sola transazione. Solo ADMIN (verificato dal BE).
  // Lettura e scrittura del set passano entrambe dal BE (niente RPC Postgres, decisione 11/09).
  public publishQuestionSet(body: IPublishBody): Observable<{ id: string; version: number }> {
    return this._http.post<{ id: string; version: number }>(
      `${this._base}/question-set/publish`,
      body,
      { headers: this._authHeaders() },
    );
  }

  // "Avvia intervista" dopo Scheda e scelta del percorso: fissa versione del questionario,
  // percorso proposto/scelto (+ motivazione) e perimetro (ambienti, impianti). 409 se già avviata.
  public startInterview(
    interviewId: string,
    body: IStartInterviewBody,
  ): Observable<{ question_set_id: string; version: number }> {
    return this._http.post<{ question_set_id: string; version: number }>(
      `${this._base}/interview/${interviewId}/start`,
      body,
      { headers: this._authHeaders() },
    );
  }

  // JWT Supabase per il proxy NestJS: senza sessione (auth disabilitata in dev) nessun header,
  // il backend risponderà 401 — segnale corretto, non va mascherato lato FE.
  private _authHeaders(): Record<string, string> {
    const token: string | undefined = this._auth.sessionSignal()?.access_token;
    if (undefined === token) {
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }

  // fetch e non HttpClient: solo fetch dà accesso al ReadableStream del body
  // (res.body.getReader()) per leggere lo streaming SSE token-per-token. HttpClient,
  // pur usando fetch come backend, consegna la risposta solo quando è completa, niente stream.
  private async _generate(sys: string, content: UserContent, maxTokens: number): Promise<string> {
    const startedAt = performance.now();
    const res: Response = await fetch(`${this._base}/proxy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this._authHeaders() },
      body: JSON.stringify({
        model: environment.model,
        max_tokens: maxTokens,
        // stream: true NON riguarda l'invio (la richiesta parte comunque tutta intera): è un
        // campo del payload letto da CLAUDE, che così risponde token-per-token (SSE) invece che
        // tutto alla fine. Il proxy inoltra ogni token appena nasce, quindi durante i 2-3 min di
        // generazione questo fetch RICEVE byte di continuo: (a) nessun timeout di nginx/proxy
        // scatta (contano il silenzio tra un byte e l'altro, non la durata totale); (b) Anthropic
        // lo richiede per output lunghi. L'app poi usa il testo solo a fine stream (v. loop
        // reader.read() sotto): niente effetto "testo che appare", è solo idraulica.
        stream: true,
        // System prompt sempre marcato cacheable (prompt caching Anthropic, no beta header):
        // ogni chiamata dell'app usa un prompt admin fisso (paraverbale, merge, cliente,
        // architetto), identico a parità di ruolo su chiamate diverse/interviste diverse — un
        // no-op sicuro se sotto la soglia minima cacheable (~1024 token), un risparmio reale
        // sopra. V. Correzione 18, 06_profilo_architettonico.tex.
        system: [{ type: 'text', text: sys, cache_control: { type: 'ephemeral' } }],
        messages: [{ role: 'user', content }],
      }),
    });
    if (false === res.ok) {
      throw new Error('HTTP ' + res.status);
    }
    const ct: string = res.headers.get('content-type') || '';
    if (true === ct.includes('application/json')) {
      const d: {
        content?: { type: string; text?: string }[];
        stop_reason?: string;
        usage?: IUsage;
        error?: { message?: string };
      } = await res.json();
      if (undefined !== d.error) {
        throw new Error(d.error.message || 'Errore API');
      }
      const text: string = (d.content ?? [])
        .filter((block) => 'text' === block.type)
        .map((block) => block.text ?? '')
        .join('')
        .trim();
      if ('' === text) {
        throw new Error(this._emptyMsg(d.stop_reason));
      }
      this._logUsage(maxTokens, startedAt, text.length, d.usage);
      return text;
    }
    if (null === res.body) {
      throw new Error('Risposta in streaming senza corpo');
    }
    // Divisione dei ruoli nello streaming (v. LaTeX sez. Proxy NestJS, "Streaming"):
    // - `await fetch` sopra si è sbloccato agli HEADER: res.body è il rubinetto ancora aperto,
    //   il testo NON è ancora arrivato.
    // - `reader.read()` (nel for sotto) riceve UN chunk alla volta, appena arriva dal proxy;
    //   non assembla niente, porge solo il pezzo successivo (done: true = rubinetto chiuso).
    // - Ad assemblare sono i due `+=`: `buf` accumula i byte e li spezza in righe SSE,
    //   `out` accumula il testo vero token dopo token. L'app usa `out` solo a fine stream.
    const reader: ReadableStreamDefaultReader<Uint8Array> = res.body.getReader();
    const dec: TextDecoder = new TextDecoder();
    let buf: string = '';
    let out: string = '';
    // stop_reason arriva nell'evento message_delta, non in message_start: catturarlo serve solo
    // per il messaggio d'errore quando out resta vuoto (v. _emptyMsg), mai per interrompere prima.
    let stopReason: string | undefined;
    // usage: input/cache_* arrivano su message_start, output_tokens si aggiorna su message_delta
    // (cumulativo) — v. _logUsage.
    let usage: IUsage = {};
    for (;;) {
      const { done, value } = await reader.read();
      if (true === done) {
        break;
      }
      buf += dec.decode(value, { stream: true });
      const lines: string[] = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (false === line.startsWith('data:')) {
          continue;
        }
        const data: string = line.slice(5).trim();
        if ('' === data || '[DONE]' === data) {
          continue;
        }
        let ev: {
          type?: string;
          message?: { usage?: IUsage };
          delta?: { type?: string; text?: string; stop_reason?: string };
          usage?: IUsage;
          error?: { message?: string };
        };
        try {
          ev = JSON.parse(data);
        } catch {
          continue;
        }
        if ('error' === ev.type) {
          throw new Error(ev.error?.message || 'Errore API');
        }
        if ('message_start' === ev.type && undefined !== ev.message?.usage) {
          usage = ev.message.usage;
        }
        if ('content_block_delta' === ev.type && 'text_delta' === ev.delta?.type) {
          out += ev.delta.text ?? '';
        }
        if ('message_delta' === ev.type) {
          if (undefined !== ev.delta?.stop_reason) {
            stopReason = ev.delta.stop_reason;
          }
          if (undefined !== ev.usage?.output_tokens) {
            usage = { ...usage, output_tokens: ev.usage.output_tokens };
          }
        }
      }
    }
    out = out.trim();
    if ('' === out) {
      throw new Error(this._emptyMsg(stopReason));
    }
    this._logUsage(maxTokens, startedAt, out.length, usage);
    return out;
  }

  // Diagnostica temporanea (v. Correzione 19): quanto tempo prende una chiamata e quanti token
  // ha davvero mosso — inclusi i token letti/scritti in cache, per capire se il prompt caching
  // (Correzione 18) sta facendo qualcosa o se le variazioni di tempo osservate sono normale
  // variabilità di lunghezza dell'output.
  private _logUsage(maxTokens: number, startedAt: number, textLen: number, usage?: IUsage): void {
    if (true === environment.production) {
      return;
    }
    const elapsedS = ((performance.now() - startedAt) / 1000).toFixed(1);
    // eslint-disable-next-line no-console -- diagnostica richiesta esplicitamente, da rimuovere
    // (o lasciare, è innocua) una volta chiarito l'impatto del caching sui tempi di Fase B.
    console.info(
      `[api.generate] ${elapsedS}s · maxTokens=${maxTokens} · testo=${textLen} char · usage=`,
      usage ?? '(nessuna, ramo JSON senza campo usage)',
    );
  }

  // "refusal" = il classificatore di sicurezza di Anthropic ha bloccato la generazione per
  // design (v. https://docs.anthropic.com/... stop_reason): un caso genuino, distinto da un
  // troncamento/hiccup di rete che restituisce comunque testo vuoto senza quel motivo esplicito.
  // Senza questa distinzione in console, ogni risposta vuota è indistinguibile dalle altre.
  private _emptyMsg(stopReason?: string): string {
    const base = 'Risposta vuota dal modello (nessun testo generato)';
    if ('refusal' === stopReason) {
      return `${base}: bloccata dal filtro di sicurezza di Anthropic (stop_reason "refusal"). Verifica il contenuto del prompt/contesto inviato.`;
    }
    if (undefined !== stopReason) {
      return `${base} (stop_reason: "${stopReason}").`;
    }
    return `${base}.`;
  }
}
